import React from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { isPortalContentKind } from "../../utils/portalAluno/contentKinds";
import type {
  EvaluationAnswers,
  EvaluationQuestion,
} from "../../@types/portalAluno";
import useContentDetails from "../../hooks/PortalAluno/useContentDetails";
import useEvaluation from "../../hooks/PortalAluno/useEvaluation";
import "./content-details.css";
import "./evaluation.css";
import "./style.css";

interface EvaluationLocationState {
  detailsTitle?: string;
  from?: string;
  fromKey?: string;
  returnState?: Record<string, unknown> | null;
}

const getEvaluationErrorMessage = (error: unknown) => {
  const responseData = (error as { response?: { data?: unknown } })?.response
    ?.data;
  return typeof responseData === "string" ? responseData : "";
};

const EvaluationPage = () => {
  const { contentId = "", contentType, evaluationId = "" } = useParams<{
    contentId: string;
    contentType?: string;
    evaluationId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as EvaluationLocationState | null;
  const isStandaloneEvaluation = !contentId && !contentType;
  const numericContentId = contentId ? Number(contentId) : undefined;
  const numericEvaluationId = Number(evaluationId);
  const sourceTypeCandidate = contentType ?? "cursos";
  const sourceType = !isStandaloneEvaluation && isPortalContentKind(sourceTypeCandidate)
    ? sourceTypeCandidate
    : undefined;
  const parentDetailsQuery = useContentDetails(
    typeof numericContentId === "number" &&
      Number.isInteger(numericContentId) &&
      numericContentId > 0
      ? numericContentId
      : undefined,
    sourceType
  );
  const evaluationQuery = useEvaluation(
    Number.isInteger(numericEvaluationId) && numericEvaluationId > 0
      ? numericEvaluationId
      : undefined
  );
  const evaluation = evaluationQuery.data;
  const parentTitle = isStandaloneEvaluation
    ? "Enquetes e Pesquisas"
    : parentDetailsQuery.data?.title ??
      navigationState?.detailsTitle ??
      "conteúdo";
  const [answers, setAnswers] = React.useState<EvaluationAnswers>({});
  const [validationMessage, setValidationMessage] = React.useState("");
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const resultHeadingRef = React.useRef<HTMLHeadingElement>(null);

  React.useEffect(() => {
    if (isSubmitted) resultHeadingRef.current?.focus();
  }, [isSubmitted]);

  React.useEffect(() => {
    setAnswers({});
    setValidationMessage("");
    setIsSubmitted(false);
  }, [numericEvaluationId]);

  const detailsPath = isStandaloneEvaluation
    ? "/app/menu/enquetes"
    : sourceType
    ? `/app/conteudo/detalhes/${sourceType}/${numericContentId}`
    : "/app";
  const backLabel = isStandaloneEvaluation
    ? "Voltar às enquetes"
    : "Voltar ao curso";
  const finishLabel = isStandaloneEvaluation
    ? "Concluir e voltar às enquetes"
    : "Concluir e voltar ao curso";
  const handleBack = React.useCallback(() => {
    navigate(navigationState?.from ?? detailsPath, {
      state: {
        ...(navigationState?.returnState ?? {}),
        ...(navigationState?.fromKey
          ? { restoreScrollFromKey: navigationState.fromKey }
          : {}),
      },
    });
  }, [detailsPath, navigate, navigationState]);

  if (
    (!isStandaloneEvaluation &&
      (!sourceType ||
        typeof numericContentId !== "number" ||
        !Number.isInteger(numericContentId) ||
        numericContentId <= 0)) ||
    !Number.isInteger(numericEvaluationId) ||
    numericEvaluationId <= 0
  ) {
    return (
      <section className="content-page evaluation-page">
        <div className="content-details-page__error" role="alert">
          <h1>Avaliação inválida</h1>
          <button type="button" onClick={() => navigate("/app")}>Voltar para a Home</button>
        </div>
      </section>
    );
  }

  if (
    evaluationQuery.isLoading ||
    (!evaluation && !evaluationQuery.isError)
  ) {
    return (
      <section className="content-page evaluation-page" aria-busy="true" aria-label="Carregando avaliação">
        <div className="evaluation-page__inner">
          <Skeleton width={130} height={30} />
          <Skeleton width="min(620px, 85vw)" height={44} />
          <Skeleton width={360} height={22} />
          <Skeleton count={3} height={190} />
        </div>
      </section>
    );
  }

  if (evaluationQuery.isError || !evaluation || evaluation.questions.length === 0) {
    return (
      <section className="content-page evaluation-page">
        <div className="content-details-page__error" role="alert">
          <h1>Não foi possível carregar a avaliação</h1>
          <p>A avaliação não possui perguntas disponíveis ou ocorreu um erro na conexão.</p>
          <div>
            <button type="button" onClick={() => evaluationQuery.refetch()}>Tentar novamente</button>
            <button type="button" onClick={handleBack}>Voltar</button>
          </div>
        </div>
      </section>
    );
  }

  const answeredCount = evaluation.questions.filter(
    (question) => answers[question.id]?.trim()
  ).length;
  const objectiveQuestions = evaluation.questions.filter(
    (question) => question.type === "single-choice"
  );
  const correctAnswers = objectiveQuestions.filter((question) =>
    question.options?.some(
      (option) => option.id === answers[question.id] && option.isCorrect
    )
  ).length;
  const score = objectiveQuestions.length
    ? (10 / objectiveQuestions.length) * correctAnswers
    : 0;
  const shouldShowScore = evaluation.showScore && objectiveQuestions.length > 0;
  const shouldShowFeedback = shouldShowScore && evaluation.showFeedback;

  const updateAnswer = (questionId: string, value: string) => {
    if (isSubmitted) return;

    setAnswers((current) => ({ ...current, [questionId]: value }));
    setValidationMessage("");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const firstUnansweredQuestion = evaluation.questions.find(
      (question) => !answers[question.id]?.trim()
    );

    if (firstUnansweredQuestion) {
      setValidationMessage(
        `Responda todas as perguntas. Ainda faltam ${
          evaluation.questions.length - answeredCount
        }.`
      );
      document
        .getElementById(`evaluation-question-${firstUnansweredQuestion.id}`)
        ?.focus();
      return;
    }

    setValidationMessage("");
    try {
      await evaluationQuery.submit({ answers, evaluation });
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setValidationMessage(
        getEvaluationErrorMessage(error).includes("já realizada")
          ? "Essa avaliação já havia sido concluída anteriormente. Nenhuma resposta nova foi salva."
          : "Não foi possível enviar a avaliação. Confira sua conexão e tente novamente."
      );
    }
  };

  const getQuestionStatus = (question: EvaluationQuestion) => {
    if (
      !isSubmitted ||
      !shouldShowFeedback ||
      question.type === "text"
    ) {
      return null;
    }

    return question.options?.some(
      (option) => option.id === answers[question.id] && option.isCorrect
    )
      ? "correct"
      : "incorrect";
  };

  return (
    <section
      className="content-page evaluation-page"
      aria-labelledby="evaluation-title"
    >
      <div className="evaluation-page__inner">
        <button
          type="button"
          className="evaluation-page__back"
          onClick={handleBack}
          aria-label={`Voltar para ${parentTitle}`}
        >
          <ArrowBackRoundedIcon aria-hidden="true" />
          <span>{backLabel}</span>
        </button>

        <header className="content-details-page__header evaluation-page__header">
          <h1 id="evaluation-title">{evaluation.title}</h1>

          <div className="content-details-page__meta evaluation-page__meta">
            <span className="evaluation-page__type">
              <FactCheckOutlinedIcon aria-hidden="true" />
              {evaluation.type}
            </span>
            <span className="content-details-page__divider" aria-hidden="true" />
            <span>{evaluation.questions.length} perguntas</span>
            <span className="evaluation-page__time">
              <ScheduleRoundedIcon aria-hidden="true" />
              Cerca de {evaluation.estimatedTime}
            </span>
          </div>
          <p className="evaluation-page__description">{evaluation.description}</p>
        </header>

        {isSubmitted && (
          <section className="evaluation-result" aria-labelledby="evaluation-result-title">
            <div className="evaluation-result__icon" aria-hidden="true">
              <TaskAltRoundedIcon />
            </div>
            <div className="evaluation-result__copy">
              <span>Respostas enviadas</span>
              <h2 id="evaluation-result-title" ref={resultHeadingRef} tabIndex={-1}>
                Avaliação realizada!
              </h2>
              <p>
                {shouldShowScore
                  ? `Você acertou ${correctAnswers} de ${objectiveQuestions.length} ${
                      objectiveQuestions.length === 1
                        ? "questão objetiva"
                        : "questões objetivas"
                    }.${
                      shouldShowFeedback
                        ? " Confira o feedback de cada resposta abaixo."
                        : " O feedback será disponibilizado após o encerramento da avaliação."
                    }`
                  : "Suas respostas foram registradas."}
              </p>
            </div>
            {shouldShowScore && (
              <div className="evaluation-result__score" aria-label={`Nota final ${score.toFixed(1)} de 10`}>
                <span>Nota final</span>
                <strong>{score.toFixed(1).replace(".", ",")}</strong>
                <small>de 10</small>
              </div>
            )}
          </section>
        )}

        <div className="evaluation-layout">
          <form className="evaluation-form" noValidate onSubmit={handleSubmit}>
            <div className="evaluation-form__heading">
              <div>
                <span>{isSubmitted ? "Revisão" : "Questionário"}</span>
                <h2>
                  {isSubmitted
                    ? "Confira suas respostas"
                    : "Responda com atenção"}
                </h2>
              </div>
              <span className="evaluation-form__counter">
                {answeredCount}/{evaluation.questions.length} respondidas
              </span>
            </div>

            <div className="evaluation-questions">
              {evaluation.questions.map((question, questionIndex) => {
                const questionStatus = getQuestionStatus(question);

                return (
                  <article
                    id={`evaluation-question-${question.id}`}
                    className={`evaluation-question${
                      questionStatus
                        ? ` evaluation-question--${questionStatus}`
                        : ""
                    }`}
                    key={question.id}
                    tabIndex={-1}
                    aria-labelledby={`evaluation-question-title-${question.id}`}
                  >
                    <div className="evaluation-question__heading">
                      <span className="evaluation-question__number" aria-hidden="true">
                        {String(questionIndex + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <span className="evaluation-question__type">
                          {question.type === "text"
                            ? "Resposta dissertativa"
                            : "Selecione uma alternativa"}
                        </span>
                        <h3 id={`evaluation-question-title-${question.id}`}>
                          {question.title}
                        </h3>
                        {question.description && <p>{question.description}</p>}
                      </div>
                    </div>

                    {question.mediaUrl && (
                      <div className="evaluation-question__media">
                        {/\.(?:png|jpe?g|gif|webp)(?:[?#].*)?$/i.test(question.mediaUrl) ? (
                          <img src={question.mediaUrl} alt={`Mídia da pergunta ${questionIndex + 1}`} />
                        ) : /\.(?:mp4|mov|m4v|webm)(?:[?#].*)?$/i.test(question.mediaUrl) ? (
                          <video src={question.mediaUrl} controls preload="metadata" />
                        ) : /\.(?:mp3|m4a|aac|wav|ogg|oga|flac)(?:[?#].*)?$/i.test(question.mediaUrl) ? (
                          <audio src={question.mediaUrl} controls preload="metadata" />
                        ) : (
                          <a
                            href={question.mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Abrir material da pergunta
                          </a>
                        )}
                      </div>
                    )}

                    {question.type === "single-choice" ? (
                      <fieldset className="evaluation-options">
                        <legend className="sr-only">{question.title}</legend>
                        {question.options?.map((option, optionIndex) => {
                          const isSelected = answers[question.id] === option.id;
                          const shouldShowCorrect =
                            isSubmitted && shouldShowFeedback && option.isCorrect;
                          const shouldShowIncorrect =
                            isSubmitted &&
                            shouldShowFeedback &&
                            isSelected &&
                            !option.isCorrect;

                          return (
                            <label
                              className={`evaluation-option${
                                isSelected ? " evaluation-option--selected" : ""
                              }${
                                shouldShowCorrect
                                  ? " evaluation-option--correct"
                                  : ""
                              }${
                                shouldShowIncorrect
                                  ? " evaluation-option--incorrect"
                                  : ""
                              }`}
                              key={option.id}
                            >
                              <input
                                type="radio"
                                name={question.id}
                                value={option.id}
                                checked={isSelected}
                                disabled={isSubmitted}
                                onChange={() => updateAnswer(question.id, option.id)}
                              />
                              <span className="evaluation-option__letter" aria-hidden="true">
                                {String.fromCharCode(65 + optionIndex)}
                              </span>
                              <span className="evaluation-option__label">{option.label}</span>
                              {shouldShowCorrect && (
                                <CheckCircleRoundedIcon
                                  className="evaluation-option__result-icon"
                                  aria-label="Resposta correta"
                                />
                              )}
                              {shouldShowIncorrect && (
                                <CloseRoundedIcon
                                  className="evaluation-option__result-icon"
                                  aria-label="Resposta incorreta"
                                />
                              )}
                            </label>
                          );
                        })}
                      </fieldset>
                    ) : (
                      <label className="evaluation-text-answer">
                        <span className="sr-only">Sua resposta</span>
                        <textarea
                          value={answers[question.id] ?? ""}
                          disabled={isSubmitted}
                          maxLength={600}
                          placeholder="Digite sua resposta aqui..."
                          onChange={(event) =>
                            updateAnswer(question.id, event.target.value)
                          }
                        />
                        <span>{(answers[question.id] ?? "").length}/600</span>
                      </label>
                    )}

                    {isSubmitted &&
                      shouldShowFeedback &&
                      question.type === "single-choice" && (
                      <div
                        className={`evaluation-question__feedback evaluation-question__feedback--${questionStatus}`}
                      >
                        {questionStatus === "correct" ? (
                          <CheckCircleRoundedIcon aria-hidden="true" />
                        ) : (
                          <ErrorOutlineRoundedIcon aria-hidden="true" />
                        )}
                        <div>
                          <strong>
                            {questionStatus === "correct"
                              ? "Resposta correta"
                              : "Resposta incorreta"}
                          </strong>
                          <p>{question.feedback}</p>
                        </div>
                      </div>
                    )}

                    {isSubmitted && question.type === "text" && (
                      <div className="evaluation-question__feedback evaluation-question__feedback--pending">
                        <CheckCircleRoundedIcon aria-hidden="true" />
                        <div>
                          <strong>Resposta registrada</strong>
                          <p>A resposta dissertativa não compõe a nota automática.</p>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <div className="evaluation-form__footer">
              {validationMessage && (
                <div className="evaluation-form__validation" role="alert">
                  <ErrorOutlineRoundedIcon aria-hidden="true" />
                  <span>{validationMessage}</span>
                </div>
              )}

              <div className="evaluation-form__actions">
                <button
                  type="button"
                  className="evaluation-button evaluation-button--secondary"
                  onClick={handleBack}
                >
                  <ArrowBackRoundedIcon aria-hidden="true" />
                  {backLabel}
                </button>
                {isSubmitted ? (
                  <button
                    type="button"
                    className="evaluation-button evaluation-button--primary"
                    onClick={handleBack}
                  >
                    {finishLabel}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="evaluation-button evaluation-button--primary"
                    disabled={evaluationQuery.isSubmitting}
                  >
                    {evaluationQuery.isSubmitting ? "Enviando..." : "Enviar avaliação"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default EvaluationPage;
